import * as React from 'react';
import {observer} from 'mobx-react';
import {style} from 'typestyle';
import {betweenJustified, center, centerCenter, horizontal, horizontallySpaced, vertical} from 'csstips';
import {px, rem, url} from 'csx';
import {colorBackground, colorBrand, colorSubtle} from '../theme/color';
import {resetAnchorStyles} from '../theme/reset';
import {RatecardGauge} from './ratecard-gauge';
import {PageCheckoutPanelProps} from './page-checkout-panels';
import {ButtonPill} from './button-pill';
import {PageCheckoutCouponCode} from './page-checkout-coupon-code';
import {Alert} from './alert';
import {getChannelImageURL} from '../lib/image';

export interface PageCheckoutPanelStep0Props extends PageCheckoutPanelProps {
  onSelectChannel(): void;
  onChangeCouponCode?(couponCode: string): void;
  i18n?: {
    title: string;
    channel: string;
    period_label: string;
    period_value(start: Date, end: Date): string;
    viewership_label: string;
    viewership_value(views: number): string;
    num_videos_label: string;
    num_videos_value(videos: number): string;
    price_band: string;
    price_per_track: string;
    in_basket_label: string;
    in_basket_value(count: number): string;
    next: string;
    titleVat: string;
    titleCoupon: string;
  }
}

const DEFAULT_I18N = {
  title: 'Your account',
  channel: 'YouTube channel',
  period_label: 'Calculation period',
  period_value: (start: Date, end: Date) => `${start.toLocaleDateString('en-GB', { month: 'short', day: '2-digit', year: 'numeric' })} - ${end.toLocaleDateString('en-GB', { month: 'short', day: '2-digit', year: 'numeric' })}`,
  viewership_label: 'Viewership',
  viewership_value: (views: number) => `${views} ${views === 1 ? 'view' : 'views'}`,
  num_videos_label: 'Number of videos',
  num_videos_value: (videos: number) => `${videos} ${videos === 1 ? 'video' : 'videos'}`,
  price_band: 'Price band',
  price_per_track: 'Price per track',
  in_basket_label: 'Tracks in basket',
  in_basket_value: (count: number) => `${count<10?'0':''}${count}`,
  next: 'Continue',
  titleVat: 'VAT number',
  titleCoupon: 'Discount'
};

export const PageCheckoutPanelStep0 = observer<PageCheckoutPanelStep0Props>(
  ({ model, i18n = DEFAULT_I18N, onSelectChannel, onChangeCouponCode }) => {
    return (
      <div className={styles.container}>
        <h5>{i18n.title}</h5>
        <div className={styles.account}>
          <div style={{ backgroundImage: url(getChannelImageURL(model.channel)) }} />
          <div>
            <div className={styles.label}>{i18n.channel}</div>
            <div>
              <a className={styles.link} href={`https://www.youtube.com/channel/${model.channel.id}`} target="_blank">
                {model.channel.name || model.channel.id}
              </a>
            </div>
            <div className={styles.change}>
              <ButtonPill onClick={onSelectChannel}>
                Change channel
              </ButtonPill>
            </div>
          </div>
        </div>
        <RatecardGauge
          className={styles.period}
          ratecard={model.channel.ratecard}
          statistics={model.channel.ratecard_statistics}
        />
        {onChangeCouponCode && (
          <div className={styles.section}>
            <h5>{i18n.titleCoupon}</h5>
            <PageCheckoutCouponCode
              model={model.couponForm}
              onChange={onChangeCouponCode}
            />
            {onChangeCouponCode && model.couponText && (
              <Alert title={model.couponText.title} type="warning" position="top-left">
                <p>{model.couponText.message}</p>
              </Alert>
            )}
          </div>
        )}
      </div>
    )
  }
);

const circleRadius = rem(4.8);

const styles = {
  container: style({
    ...vertical,
    flex: 1,
    $nest: {
      '& h5': {
        margin: '0 0 1rem 0'
      }
    }
  }),
  account: style({
    ...horizontal,
    ...horizontallySpaced(15),
    ...center,
    padding: `0 0 0.75rem 0`,
    borderBottom: `1px solid ${colorBackground.fade(0.7).toString()}`,
    $nest: {
      '&> div:first-child': {
        flexShrink: 0,
        width: circleRadius,
        height: circleRadius,
        borderRadius: circleRadius,
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat'
      },
      '&> div:last-child': {
        overflow: 'hidden'
      },
      '&> div > div': {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'pre'
      }
    }
  }),
  label: style({
    color: colorSubtle.toString(),
    fontSize: px(14),
    lineHeight: px(24)
  }),
  section: style({
    borderTop: `1px solid ${colorBackground.fade(0.7).toString()}`,
    padding: '1rem 0'
  }),
  change: style({
    margin: '0.5rem 0 0 0'
  }),
  link: style({
    color: colorBrand.toString(),
    fontWeight: 'bold',
    fontSize: rem(1.125),
    lineHeight: rem(1.2),
    $nest: {
      ...resetAnchorStyles(colorBrand.toString())
    }
  }),
  period: style({
    margin: 0,
    padding: '1rem 0',
    borderBottom: `1px solid ${colorBackground.fade(0.7).toString()}`,
    $nest: {
      '& dt': {
        margin: 0,
        padding: 0,
        color: colorSubtle.toString(),
        fontSize: rem(0.875),
        lineHeight: rem(1.2)
      },
      '& dd': {
        margin: '0 0 1rem 0',
        padding: 0,
        fontSize: rem(1),
        fontWeight: 'bold',
        lineHeight: rem(1.2)
      },
      '& dd:last-child': {
        margin: 0
      }
    }
  }),
  totals: style({
    ...horizontal,
    ...betweenJustified,
    flex: 1,
    padding: '1rem 0',
    $nest: {
      '& > div > span': {
        color: colorSubtle.toString(),
        fontSize: rem(0.875),
        lineHeight: rem(1.2),
        display: 'block',
        width: rem(3)
      },
      '& > div > div': {
        ...centerCenter,
        fontSize: rem(1.6),
        lineHeight: rem(1.7),
        fontWeight: 'bold',
        flexShrink: 0,
        margin: '0.25rem 0 0 0',
        width: circleRadius,
        height: circleRadius,
        borderRadius: circleRadius,
        border: `1px solid ${colorBackground.fade(0.7).toString()}`
      }
    }
  }),
  totalsPrice: style({
    width: 'auto !important',
    padding: '0 0.5rem'
  })
};